// Typed collection references — one place to declare paths + converters.

import {
  type CollectionReference,
  type DocumentReference,
  type Query,
  collection,
  collectionGroup,
  doc,
} from "firebase/firestore";
import { getFirebase } from "./client";
import {
  audioConverter,
  configEventConverter,
  echoConverter,
  fragmentConverter,
  listenConverter,
  markerConverter,
  markerDocId,
  messageConverter,
  pinConverter,
  regionConverter,
  userConverter,
} from "./converters";
import type {
  AudioDoc,
  ConfigEventDoc,
  EchoDoc,
  FragmentDoc,
  ListenDoc,
  MarkerDoc,
  MessageDoc,
  PinDoc,
  RegionDoc,
  UserDoc,
} from "./types";

export function usersCol(): CollectionReference<UserDoc> {
  return collection(getFirebase().db, "users").withConverter(userConverter);
}

export function userDoc(userId: string): DocumentReference<UserDoc> {
  return doc(getFirebase().db, "users", userId).withConverter(userConverter);
}

export function audiosCol(): CollectionReference<AudioDoc> {
  return collection(getFirebase().db, "audios").withConverter(audioConverter);
}

export function audioDoc(audioId: string): DocumentReference<AudioDoc> {
  return doc(getFirebase().db, "audios", audioId).withConverter(audioConverter);
}

/** /audios/{audioId}/listens/{userId} — learner marks an audio as opened. */
export function listenDoc(
  audioId: string,
  userId: string,
): DocumentReference<ListenDoc> {
  return doc(
    getFirebase().db,
    "audios",
    audioId,
    "listens",
    userId,
  ).withConverter(listenConverter);
}

/** Collection-group over ALL listens (every audio × every learner). Worker
 *  uses this to compute「聽過 N/14」per audio. Each ListenDoc carries userId;
 *  the parent audio id comes from snap.ref.parent.parent.id. */
export function allListensQuery(): Query<ListenDoc> {
  return collectionGroup(getFirebase().db, "listens").withConverter(
    listenConverter,
  );
}

export function markersCol(): CollectionReference<MarkerDoc> {
  return collection(getFirebase().db, "markers").withConverter(markerConverter);
}

export function markerDoc(
  userId: string,
  regionId: string,
): DocumentReference<MarkerDoc> {
  return doc(
    getFirebase().db,
    "markers",
    markerDocId(userId, regionId),
  ).withConverter(markerConverter);
}

/**
 * Subcollection: /markers/{uid_rid}/pins/{pinId}
 *
 * Returns the typed pins collection under a specific marker. Use addDoc()
 * for new pins (auto-generated pinId) or doc() with explicit pinId for
 * migration / deterministic seeding.
 */
export function pinsCol(
  userId: string,
  regionId: string,
): CollectionReference<PinDoc> {
  return collection(
    getFirebase().db,
    "markers",
    markerDocId(userId, regionId),
    "pins",
  ).withConverter(pinConverter);
}

export function pinDoc(
  userId: string,
  regionId: string,
  pinId: string,
): DocumentReference<PinDoc> {
  return doc(
    getFirebase().db,
    "markers",
    markerDocId(userId, regionId),
    "pins",
    pinId,
  ).withConverter(pinConverter);
}

/**
 * Normalize a pin's clue photos to a single array, bridging the legacy
 * single `photoUrl` field and the 2026-05-30 multi-photo `photoUrls` array.
 * Prefers `photoUrls` when present; otherwise falls back to the legacy single
 * `photoUrl`; otherwise empty. Takes a loose shape so both PinDoc and the
 * lighter BarCluePin can pass through.
 */
export function pinPhotos(pin: {
  photoUrl?: string;
  photoUrls?: string[];
}): string[] {
  if (pin.photoUrls && pin.photoUrls.length > 0) return pin.photoUrls;
  if (pin.photoUrl) return [pin.photoUrl];
  return [];
}

/**
 * Collection-group query for ALL pins across all markers. Used by
 * useAdminSync (full read) and useUserSync (filtered by userId).
 * Requires the fieldOverride in firestore.indexes.json for userId.
 */
export function allPinsQuery(): Query<PinDoc> {
  return collectionGroup(getFirebase().db, "pins").withConverter(pinConverter);
}

export function regionsCol(): CollectionReference<RegionDoc> {
  return collection(getFirebase().db, "regions").withConverter(regionConverter);
}

export function regionDoc(regionId: string): DocumentReference<RegionDoc> {
  return doc(getFirebase().db, "regions", regionId).withConverter(
    regionConverter,
  );
}

export function configEventDoc(): DocumentReference<ConfigEventDoc> {
  return doc(getFirebase().db, "config", "event").withConverter(
    configEventConverter,
  );
}

// ── /messages ────────────────────────────────────────────────────────
// 「訊息」simple one-way bulletin from worker to learner(s).
export function messagesCol(): CollectionReference<MessageDoc> {
  return collection(getFirebase().db, "messages").withConverter(
    messageConverter,
  );
}

export function messageDoc(messageId: string): DocumentReference<MessageDoc> {
  return doc(getFirebase().db, "messages", messageId).withConverter(
    messageConverter,
  );
}

// ── /fragments ───────────────────────────────────────────────────────
// 「碎片」: richer content — text + photos + video. See FragmentDoc.
export function fragmentsCol(): CollectionReference<FragmentDoc> {
  return collection(getFirebase().db, "fragments").withConverter(
    fragmentConverter,
  );
}

export function fragmentDoc(
  fragmentId: string,
): DocumentReference<FragmentDoc> {
  return doc(getFirebase().db, "fragments", fragmentId).withConverter(
    fragmentConverter,
  );
}

// ── /echoes ──────────────────────────────────────────────────────────
// 「回 聲」: post-event learner voice reflections. See EchoDoc. Flat
// collection keyed by an auto-generated echoId; the doc body carries
// userId so the learner list (where userId==X) and the admin overview
// (whole collection grouped by userId) both work off the same docs.
export function echoesCol(): CollectionReference<EchoDoc> {
  return collection(getFirebase().db, "echoes").withConverter(echoConverter);
}

export function echoDoc(echoId: string): DocumentReference<EchoDoc> {
  return doc(getFirebase().db, "echoes", echoId).withConverter(echoConverter);
}

/** Mint a fresh echoId up front (used for the Storage path BEFORE the doc
 *  exists — fragment-style: the raw upload encodes this id, the CF product
 *  lands at echoes/{userId}/{echoId}.m4a, and only then do we setDoc the
 *  EchoDoc under this same id). */
export function newEchoId(): string {
  return doc(echoesCol()).id;
}
